/**
 * Typed BFF client + pure helpers for the AI advisor, expert review and expert-call booking (track T10).
 * Server: /v1/audit-remediation/advisor/* and /v1/audit-remediation/expert/* (apps/api .../audit-remediation/{advisor,expert}).
 * Everything here is pure or takes an injectable fetch, so it is testable offline.
 */
import { bff, BffError, type FetchLike } from '../../../../../lib/bff';

export type SuggestedAction = 'resolve' | 'assisted_content' | 'book_expert';
export type AdvisorFlag = 'ungrounded' | 'regulatory' | 'refused' | 'injection_seen';

export interface AdvisorMessage {
  id: string; threadId: string; role: 'user' | 'advisor' | 'expert'; authorId: string | null; body: string; citations: string[]; createdAt: string; flags: AdvisorFlag[];
}
export interface AdvisorThread { id: string; jobId: string | null; findingId: string | null; title: string; createdBy: string; createdAt: string; updatedAt: string }
export interface AdvisorQuota { tier: 'essential' | 'professional' | 'enterprise'; day: string; questionsUsed: number; questionsLimit: number; remaining: number }
export interface AdvisorState { thread: AdvisorThread | null; messages: AdvisorMessage[]; quota: AdvisorQuota; suggestedActions: SuggestedAction[] }
export interface AskResult { thread: AdvisorThread; question: AdvisorMessage; answer: AdvisorMessage; suggestedActions: SuggestedAction[]; quota: AdvisorQuota }

export interface SignoffBadge { reviewerName: string; signedAt: string; summary: { en: string; ar: string | null } }
export type ReviewStatus = 'not_requested' | 'awaiting_payment' | 'awaiting_review' | 'changes_requested' | 'approved';
export interface CustomerNote { id: string; findingId: string | null; body: string; createdAt: string; updatedAt: string }
export interface SeverityOverride { findingId: string; from: string; to: string; reason: string; at: string }
export interface ExpertView {
  review: { tier: AdvisorQuota['tier']; included: boolean; status: ReviewStatus; signoff: SignoffBadge | null };
  notes: CustomerNote[];
  severityOverrides: SeverityOverride[];
}
export interface CheckoutHandoff { kind: 'expert_call' | 'expert_review'; ref: string; priceId: string; amountMinor: number; currency: string; jobId?: string }
export interface Booking {
  id: string; jobId: string | null; findingId: string | null; topic: string; status: 'requested' | 'payment_pending' | 'confirmed' | 'completed' | 'cancelled';
  scheduledAt: string | null; durationMin: number; priceMinor: number; currency: string; paymentRef: string | null; expertId: string | null;
  requestedBy: string; createdAt: string; updatedAt: string; preferredSlots: string[];
}
export interface BookingInput { jobId?: string; findingId?: string; topic: string; durationMin: 30 | 60; preferredSlots: string[] }

const enc = encodeURIComponent;
const defaultFetch: FetchLike = (i, r) => fetch(i, r);

export function advisorApi(f: FetchLike = defaultFetch) {
  const post = <T>(path: `/v1/${string}`, body: unknown, headers: Record<string, string> = {}) => bff<T>(path, { method: 'POST', body: JSON.stringify(body), headers }, f);
  return {
    thread: (jobId: string, findingId: string) => bff<AdvisorState>(`/v1/audit-remediation/advisor/jobs/${enc(jobId)}/findings/${enc(findingId)}`, {}, f),
    ask: (b: { jobId: string; findingId: string; question: string; locale: 'en' | 'ar'; threadId?: string }) => post<AskResult>('/v1/audit-remediation/advisor/ask', b),
    expert: (jobId: string) => bff<ExpertView>(`/v1/audit-remediation/expert/jobs/${enc(jobId)}`, {}, f),
    requestReview: (jobId: string) => post<{ status: ReviewStatus; included: boolean; checkout: CheckoutHandoff | null }>(`/v1/audit-remediation/expert/jobs/${enc(jobId)}/review-request`, {}),
    availability: (durationMin: 30 | 60, days = 14) => bff<{ durationMin: number; timeZone: string; slots: string[] }>(`/v1/audit-remediation/expert/availability?durationMin=${durationMin}&days=${days}`, {}, f),
    bookings: () => bff<Booking[]>('/v1/audit-remediation/expert/bookings', {}, f),
    book: (b: BookingInput, idempotencyKey: string) => post<{ booking: Booking; checkout: CheckoutHandoff | null }>('/v1/audit-remediation/expert/bookings', b, { 'idempotency-key': idempotencyKey }),
    cancel: (id: string, reason?: string) => post<Booking>(`/v1/audit-remediation/expert/bookings/${enc(id)}/cancel`, reason ? { reason } : {}),
  };
}
export type AdvisorApi = ReturnType<typeof advisorApi>;

export const QUESTION_MAX = 2000;
export const TOPIC_MIN = 3;
export const TOPIC_MAX = 500;
export const MAX_SLOTS = 5;

/** i18n key for a failed advisor call. */
export function advisorErrorKey(e: unknown): string {
  if (!(e instanceof BffError)) return 'audit-remediation.advisor.errors.generic';
  const reason = (e.envelope?.error.details as { reason?: string } | undefined)?.reason;
  if (e.status === 429) return 'audit-remediation.advisor.errors.dailyQuestions';
  if (e.status === 402) return reason === 'advisor_daily_spend' ? 'audit-remediation.advisor.errors.dailySpend' : 'audit-remediation.advisor.errors.budget';
  if (e.status === 404 && e.envelope?.error.code === 'feature_disabled') return 'audit-remediation.advisor.errors.unavailable';
  if (e.status === 403) return 'audit-remediation.advisor.errors.forbidden';
  if (e.status === 400) return 'audit-remediation.advisor.errors.invalid';
  return 'audit-remediation.advisor.errors.generic';
}

/** i18n key for a failed booking / review call. */
export function bookingErrorKey(e: unknown): string {
  if (!(e instanceof BffError)) return 'audit-remediation.expert.errors.generic';
  const reason = (e.envelope?.error.details as { reason?: string } | undefined)?.reason;
  if (reason === 'slot_unavailable') return 'audit-remediation.expert.errors.slotUnavailable';
  if (reason === 'price_not_configured') return 'audit-remediation.expert.errors.priceNotConfigured';
  if (reason === 'cancellation_window_closed') return 'audit-remediation.expert.errors.cancelWindow';
  if (reason === 'not_live') return 'audit-remediation.expert.errors.notLive';
  if (e.status === 403) return 'audit-remediation.expert.errors.forbidden';
  if (e.status === 404) return 'audit-remediation.expert.errors.unavailable';
  return 'audit-remediation.expert.errors.generic';
}

/** Ask-form validation: returns an i18n key or null. */
export function questionProblem(q: string): string | null {
  const s = q.trim();
  if (s.length < 2) return 'audit-remediation.advisor.form.tooShort';
  if (s.length > QUESTION_MAX) return 'audit-remediation.advisor.form.tooLong';
  return null;
}

/** New thread state after an answer (append both turns, take the server's quota and suggestions). */
export function applyAnswer(state: AdvisorState, r: AskResult): AdvisorState {
  return { thread: r.thread, messages: [...state.messages, r.question, r.answer], quota: r.quota, suggestedActions: r.suggestedActions };
}

/** Booking form validation: field -> i18n key. */
export function bookingProblems(b: { topic: string; slots: readonly string[] }): Partial<Record<'topic' | 'slots', string>> {
  const out: Partial<Record<'topic' | 'slots', string>> = {};
  const t = b.topic.trim();
  if (t.length < TOPIC_MIN || t.length > TOPIC_MAX) out.topic = 'audit-remediation.expert.book.topicInvalid';
  if (b.slots.length < 1 || b.slots.length > MAX_SLOTS) out.slots = 'audit-remediation.expert.book.slotsInvalid';
  return out;
}

/** Toggle a preferred slot, keeping at most MAX_SLOTS in time order. */
export function toggleSlot(selected: readonly string[], slot: string): string[] {
  if (selected.includes(slot)) return selected.filter((s) => s !== slot);
  if (selected.length >= MAX_SLOTS) return [...selected];
  return [...selected, slot].sort();
}

const fmt = (locale: 'en' | 'ar', o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', { timeZone: 'Asia/Dubai', ...o });

/** Slots grouped by Dubai calendar day, labelled for the viewer's locale. */
export function groupSlotsByDay(slots: readonly string[], locale: 'en' | 'ar'): { day: string; label: string; slots: { iso: string; label: string }[] }[] {
  const dayKey = fmt('en', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const dayLabel = fmt(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const timeLabel = fmt(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const out: { day: string; label: string; slots: { iso: string; label: string }[] }[] = [];
  for (const iso of slots) {
    const d = new Date(iso);
    const key = dayKey.format(d);
    let g = out.find((x) => x.day === key);
    if (!g) { g = { day: key, label: dayLabel.format(d), slots: [] }; out.push(g); }
    g.slots.push({ iso, label: timeLabel.format(d) });
  }
  return out;
}

export const formatWhen = (iso: string, locale: 'en' | 'ar') => fmt(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
export const formatDay = (iso: string, locale: 'en' | 'ar') => fmt(locale, { dateStyle: 'long' }).format(new Date(iso));

/** Client mirror of the server's cancellation rule (the server decides). */
export function customerCanCancel(b: Pick<Booking, 'status' | 'scheduledAt'>, now: Date): boolean {
  if (!['payment_pending', 'requested', 'confirmed'].includes(b.status)) return false;
  return b.status !== 'confirmed' || !b.scheduledAt || Date.parse(b.scheduledAt) - now.getTime() >= 24 * 3_600_000;
}

/** Display text of an advisor message: refusals show the translated refusal instead of the stored text. */
export function messageNotes(m: AdvisorMessage): string[] {
  const keys: string[] = [];
  if (m.flags.includes('regulatory')) keys.push('audit-remediation.advisor.note.regulatory');
  if (m.flags.includes('ungrounded')) keys.push('audit-remediation.advisor.note.ungrounded');
  return keys;
}

export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
